import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton, LabelAndValue } from '@medtronic/pecc-react-component-library-js';
import { Box, Grid, Modal } from '@mui/material';
import { RequestDatasetQuery } from '~/api/v2/endpoints/serviceNow';
import { useGetDatasetQuery, useRequestDatasetMutation } from '~/api/v2/hooks';
import { ButtonSpinner, ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';
import { closeModalById } from '~/redux/slices/modalSlice';
import { requestAccessSchema } from '~/validation';
import { RequestAccessForm } from '.';

export interface RequestDatasetAccessModalProps {
  payload: {
    datasetId: number;
    id: string;
  };
}

const RequestDatasetAccessModal: FC<RequestDatasetAccessModalProps> = ({ payload: { datasetId, id } }) => {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(selectCurrentUser);
  const { data: dataset } = useGetDatasetQuery({ datasetId });
  const [requestDataset, { isLoading }] = useRequestDatasetMutation({
    fixedCacheKey: 'request-dataset',
  });

  const formMethods = useForm<RequestDatasetQuery>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(requestAccessSchema),
    values: {
      user_id: userInfo?.profile?.NTID as string,
      data_classification: dataset?.data_classification as string,
      controlling_ad_group: dataset?.active_directory_name as string,
      data_owners: [
        dataset?.data_owner_network_id || null,
        ...(dataset?.secondary_data_owner_network_ids || []),
      ].filter(Boolean) as string[],
      dataset_name: String(dataset?.name),
      org_unit: dataset?.source_ou as string,
      dataset_primary_key: String(datasetId),
      cost_center: '',
      business_value: '',
      primary_secondary_use_case: [],
      use_case_description: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = formMethods;

  const handleClose = () => {
    if (!isLoading) {
      dispatch(closeModalById(id));
      reset();
    }
  };

  const dataTable = [
    { label: 'dataset name', value: dataset?.name },
    { label: 'data source type', value: dataset?.data_format?.join(', ') },
    { label: 'anatomy', value: dataset?.anatomy?.join(', ') },
    { label: 'modality', value: dataset?.modality?.join(', ') },
    { label: 'size (GB)', value: `${dataset?.size}` },
  ];

  const onSubmit = async (data: RequestDatasetQuery) => {
    await requestDataset(data);
    handleClose();
  };

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader onClose={handleClose} title={['Request Access', dataset?.name || '']} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Grid container width="100%">
                {dataTable.map(({ label, value }) => (
                  <Grid size={12 / 5} key={label}>
                    <LabelAndValue label={label} value={value || '—'} />
                  </Grid>
                ))}
              </Grid>
              <RequestAccessForm />
            </Box>
            <Box sx={modalActionBlockStyles}>
              <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} disabled={isLoading} />
              <CustomButton type="submit" disabled={!isValid || isLoading}>
                Request
                {isLoading && <ButtonSpinner />}
              </CustomButton>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default RequestDatasetAccessModal;
